// src/hooks/useAI.js
// ✅ Guard piano — non carica se aiEnabled=false
// ✅ sendMessage crea conversazione atomicamente se mancante
// ✅ loadSuggestions con graceful fallback su quota esaurita

import { useState, useEffect, useCallback, useRef } from 'react';
import { aiApi } from '../api/aiApi';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

export const useAI = () => {
    const { hasFeature, isSuperAdmin } = useAuth();
    const aiEnabled = isSuperAdmin || hasFeature('aiEnabled');

    const [conversations, setConversations] = useState([]);
    const [currentConversation, setCurrentConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [suggestions, setSuggestions] = useState(null);
    const [aiUsage, setAiUsage] = useState(null);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);

    const isMounted = useRef(true);
    const hasFetched = useRef(false);

    // ==================== LOAD ====================

    const loadConversations = useCallback(async () => {
        if (!isMounted.current || !aiEnabled) return;
        try {
            setLoading(true);
            const response = await aiApi.getConversations();
            if (isMounted.current) setConversations(response.data || []);
        } catch {
            // silenzioso
        } finally {
            if (isMounted.current) setLoading(false);
        }
    }, [aiEnabled]);

    const loadConversation = useCallback(async (id) => {
        if (!isMounted.current) return;
        try {
            setLoading(true);
            const response = await aiApi.getConversation(id);
            if (isMounted.current) {
                setCurrentConversation(response.data);
                setMessages(response.data.messages || []);
                setError(null);
            }
        } catch (err) {
            const message = err?.response?.data?.message || 'Errore caricamento conversazione';
            if (isMounted.current) setError(message);
            toast.error(message);
        } finally {
            if (isMounted.current) setLoading(false);
        }
    }, []);

    const loadSuggestions = useCallback(async () => {
        if (!isMounted.current || !aiEnabled) return;
        try {
            const response = await aiApi.getSuggestions();
            if (isMounted.current) {
                setSuggestions({ ...response.data, timestamp: new Date().toISOString() });
            }
        } catch (err) {
            const code = err?.response?.data?.code;
            // Non mostrare toast per quota/piano — l'UI lo gestisce già
            if (code !== 'AI_QUOTA_EXCEEDED' && code !== 'AI_PLAN_UPGRADE_REQUIRED') {
                console.warn('loadSuggestions:', err?.message);
            }
        }
    }, [aiEnabled]);

    const loadAiUsage = useCallback(async () => {
        if (!isMounted.current) return;
        try {
            const response = await aiApi.getUsage();
            if (isMounted.current) setAiUsage(response.data);
        } catch {
            // silenzioso
        }
    }, []);

    // ==================== MUTATIONS ====================

    const createConversation = useCallback(async (title = 'Nuova conversazione') => {
        try {
            const response = await aiApi.createConversation(title);
            if (isMounted.current) {
                setConversations(prev => [response.data, ...prev]);
                setCurrentConversation(response.data);
                setMessages([]);
            }
            return { success: true, data: response.data };
        } catch (err) {
            const message = err?.response?.data?.message || 'Errore creazione conversazione';
            toast.error(message);
            return { success: false, error: message };
        }
    }, []);

    const sendMessage = useCallback(async (message, conversationOverride = null) => {
        // Garantisce una conversazione attiva — atomico
        let conv = conversationOverride || currentConversation;
        if (!conv) {
            const result = await createConversation('Nuova conversazione');
            if (!result.success) return result;
            conv = result.data;
        }

        setSending(true);

        // UI ottimistica
        const tempId = `temp-${Date.now()}`;
        const optimistic = { id: tempId, role: 'user', content: message, createdAt: new Date().toISOString() };
        if (isMounted.current) setMessages(prev => [...prev, optimistic]);

        try {
            const response = await aiApi.sendMessage(conv.id, message);
            if (isMounted.current) {
                setMessages(prev => [
                    ...prev.filter(m => m.id !== tempId),
                    { ...optimistic, id: response.data.userMessage?.id || tempId },
                    response.data.assistantMessage,
                ]);
                // Aggiorna usage dopo ogni messaggio
                loadAiUsage();
            }
            return { success: true, data: response.data };
        } catch (err) {
            if (isMounted.current) setMessages(prev => prev.filter(m => m.id !== tempId));
            const code = err?.response?.data?.code;
            const message = err?.response?.data?.message || 'Errore invio messaggio';
            // I toast per quota/upgrade sono già gestiti da axiosConfig
            if (code !== 'AI_QUOTA_EXCEEDED' && code !== 'AI_PLAN_UPGRADE_REQUIRED') {
                toast.error(message);
            }
            return { success: false, error: message, code };
        } finally {
            if (isMounted.current) setSending(false);
        }
    }, [currentConversation, createConversation, loadAiUsage]);

    const sendMessageWithFile = useCallback(async (file, message = '', conversationOverride = null) => {
        let conv = conversationOverride || currentConversation;
        if (!conv) {
            const result = await createConversation(`Analisi: ${file.name}`);
            if (!result.success) return result;
            conv = result.data;
        }

        setSending(true);
        const tempId = `temp-file-${Date.now()}`;
        const previewText = message.trim() || `📎 ${file.name}`;
        const optimistic = {
            id: tempId, role: 'user',
            content: previewText,
            fileAttachment: { name: file.name, type: file.type, size: file.size },
            createdAt: new Date().toISOString(),
        };
        if (isMounted.current) setMessages(prev => [...prev, optimistic]);

        try {
            const response = await aiApi.sendMessageWithFile(conv.id, file, message);
            if (isMounted.current) {
                setMessages(prev => [
                    ...prev.filter(m => m.id !== tempId),
                    { ...optimistic, id: response.data.userMessage?.id || tempId },
                    response.data.assistantMessage,
                ]);
                loadAiUsage();
            }
            return { success: true, data: response.data };
        } catch (err) {
            if (isMounted.current) setMessages(prev => prev.filter(m => m.id !== tempId));
            const code = err?.response?.data?.code;
            const msg = err?.response?.data?.message || 'Errore analisi file';
            if (code !== 'AI_QUOTA_EXCEEDED' && code !== 'AI_PLAN_UPGRADE_REQUIRED') {
                toast.error(msg);
            }
            return { success: false, error: msg, code };
        } finally {
            if (isMounted.current) setSending(false);
        }
    }, [currentConversation, createConversation, loadAiUsage]);

    const deleteConversation = useCallback(async (id) => {
        try {
            await aiApi.deleteConversation(id);
            if (isMounted.current) {
                setConversations(prev => prev.filter(c => c.id !== id));
                if (currentConversation?.id === id) {
                    setCurrentConversation(null);
                    setMessages([]);
                }
            }
            toast.success('Conversazione eliminata');
            return { success: true };
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Errore eliminazione');
            return { success: false };
        }
    }, [currentConversation]);

    const analyzeProject = useCallback(async (projectId) => {
        try {
            setLoading(true);
            const response = await aiApi.analyzeProject(projectId);
            toast.success('Analisi completata!');
            return { success: true, data: response.data };
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Errore analisi');
            return { success: false };
        } finally {
            if (isMounted.current) setLoading(false);
        }
    }, []);

    // ==================== INIT ====================

    useEffect(() => {
        if (hasFetched.current || !aiEnabled) return;
        hasFetched.current = true;
        loadConversations();
        loadSuggestions();
        loadAiUsage();
        return () => { isMounted.current = false; };
    }, [aiEnabled]); // eslint-disable-line

    return {
        conversations,
        currentConversation,
        messages,
        suggestions,
        aiUsage,
        loading,
        sending,
        error,
        loadConversations,
        loadConversation,
        createConversation,
        sendMessage,
        sendMessageWithFile,
        analyzeProject,
        loadSuggestions,
        deleteConversation,
    };
};